/**
 * Artika Portal ↔ Theme language sync.
 * Sets portal_locale cookie when theme language changes so portal opens in the same language.
 * Cookie domain=.artika.com for cross-subdomain sharing (pro.artika.com + portal.artika.com).
 * Extensible: add locales to SUPPORTED_LOCALES when enabling new languages (e.g. "es" for Spanish).
 */
(function() {
  "use strict";

  const COOKIE_NAME = "portal_locale";
  const COOKIE_MAX_AGE = 31536000; // 1 year
  const LOG_PREFIX = "[artika-locale-sync]";

  // Keep in sync with portal app SUPPORTED_LOCALES env. Add "de", etc. when enabling.
  const SUPPORTED_LOCALES = ["en", "fr", "es"];

  function safeLog(level, message, error) {
    try {
      if (typeof console !== "undefined" && console[level]) {
        if (error) {
          console[level](LOG_PREFIX, message, error);
        } else {
          console[level](LOG_PREFIX, message);
        }
      }
    } catch (_) {
      /* noop */
    }
  }

  function getCookieDomain() {
    try {
      const host = (typeof window !== "undefined" && window.location?.hostname) ? window.location.hostname : "";
      const lower = String(host).toLowerCase();
      if (lower.endsWith(".artika.com") || lower === "artika.com") return ".artika.com";
      return null;
    } catch (e) {
      safeLog("warn", "getCookieDomain failed", e);
      return null;
    }
  }

  /** Normalize Shopify ISO code (e.g. fr-CA, es-MX) to portal locale. */
  function isoToPortalLocale(isoCode) {
    try {
      if (isoCode == null || typeof isoCode !== "string") return "en";
      const lower = String(isoCode).toLowerCase();
      const base = (lower.split("-")[0] || "").trim();
      if (base && SUPPORTED_LOCALES.includes(base)) return base;
      if (SUPPORTED_LOCALES.includes(lower)) return lower;
      return "en";
    } catch (e) {
      safeLog("warn", "isoToPortalLocale failed", e);
      return "en";
    }
  }

  function setPortalLocale(locale) {
    try {
      const normalized = isoToPortalLocale(locale);
      const domain = getCookieDomain();
      let cookie = COOKIE_NAME + "=" + normalized + "; path=/; max-age=" + COOKIE_MAX_AGE + "; samesite=lax";
      if (domain) cookie += "; domain=" + domain;
      if (typeof document !== "undefined") {
        document.cookie = cookie;
      }
    } catch (e) {
      safeLog("warn", "Failed to set cookie", e);
    }
  }

  /** Get current storefront locale for portal links. Use for ?locale= param. */
  function getStorefrontLocale() {
    try {
      const cookieStr = typeof document !== "undefined" ? document.cookie : "";
      const escaped = COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const cookieMatch = cookieStr ? String(cookieStr).match(new RegExp(escaped + "=([^;]+)")) : null;
      if (cookieMatch && cookieMatch[1]) {
        const val = String(cookieMatch[1]).trim().toLowerCase();
        const base = (val.split("-")[0] || "").trim();
        if (SUPPORTED_LOCALES.includes(val)) return val;
        if (base && SUPPORTED_LOCALES.includes(base)) return base;
      }
      if (typeof window !== "undefined" && window.Shopify?.language && typeof window.Shopify.language === "string") {
        return isoToPortalLocale(window.Shopify.language);
      }
      const path = (typeof window !== "undefined" && window.location?.pathname) ? window.location.pathname : "";
      const segments = String(path).split("/").filter(Boolean);
      const firstSegment = segments[0] || "";
      return isoToPortalLocale(firstSegment || "en");
    } catch (e) {
      safeLog("warn", "getStorefrontLocale failed", e);
      return "en";
    }
  }

  /**
   * Persistence: sync theme from portal cookie on return (production only).
   * When user switches language on portal and visits store root, redirect to /fr, /es, etc.
   * Runs only when both theme and portal are on *.artika.com (cookie shared).
   */
  function runPersistenceRedirect() {
    try {
      if (!getCookieDomain()) return;
      const cookieStr = typeof document !== "undefined" ? document.cookie : "";
      const escaped = COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const match = cookieStr ? String(cookieStr).match(new RegExp(escaped + "=([^;]+)")) : null;
      if (!match || !match[1]) return;

      const portalLocale = String(match[1]).trim().toLowerCase().split("-")[0];
      if (!portalLocale || portalLocale === "en" || !SUPPORTED_LOCALES.includes(portalLocale)) return;

      const path = (typeof window !== "undefined" && window.location?.pathname) ? window.location.pathname : "/";
      const segments = String(path).split("/").filter(Boolean);
      const firstSegment = segments[0] || "";
      const hasLocalePrefix = SUPPORTED_LOCALES.some(function(loc) {
        const seg = firstSegment.toLowerCase();
        return seg === loc || seg.startsWith(loc + "-");
      });
      if (hasLocalePrefix) return;

      const newPath = "/" + portalLocale + (path === "/" ? "" : path);
      if (typeof window !== "undefined" && window.location && window.location.pathname !== newPath) {
        window.location.replace(newPath + (window.location.search || ""));
      }
    } catch (e) {
      safeLog("warn", "Persistence redirect failed", e);
    }
  }

  try {
    if (typeof window !== "undefined") {
      window.ArtikaLocaleSync = { setPortalLocale, isoToPortalLocale, getStorefrontLocale };
    }

    if (typeof document !== "undefined") {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", runPersistenceRedirect);
      } else {
        runPersistenceRedirect();
      }
    }
  } catch (e) {
    safeLog("error", "Initialization failed", e);
    if (typeof window !== "undefined") {
      window.ArtikaLocaleSync = {
        setPortalLocale: function() {},
        isoToPortalLocale: function() { return "en"; },
        getStorefrontLocale: function() { return "en"; }
      };
    }
  }
})();
